import { useNavigate } from "react-router-dom";
import { useProductContext, type ProductMode } from "@/contexts/ProductContext";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

interface Props {
  /** Visual size variant. */
  size?: "sm" | "md";
  className?: string;
}

/**
 * Segmented pill that switches between LaneRunner and LR Direct product contexts.
 * Hidden for unauthenticated users.
 * Clicking a segment updates context AND navigates to the role-appropriate Dashboard.
 */
export function ProductContextSwitcher({ size = "md", className }: Props) {
  const { context, setContext, switcherEnabled } = useProductContext();
  const { profile, isAdmin } = useAuth();
  const navigate = useNavigate();

  if (!switcherEnabled) return null;

  const userType = profile?.user_type;

  // Resolve the destination Dashboard based on BOTH the target context AND role.
  // LR Direct: brokers → /lr-direct/broker, shippers → /shipper-dashboard (LR Direct).
  // LaneRunner: brokers → /broker-dashboard, shippers → /shipper-dashboard-lanerunner.
  const destinationFor = (next: ProductMode): string => {
    if (userType === "carrier") return next === "lr-direct" ? "/lr-direct/broker" : "/carrier-dashboard";
    if (next === "lr-direct") {
      if (userType === "shipper") return "/shipper-dashboard";
      // broker, broker_carrier, admin
      return "/lr-direct/broker";
    }
    // next === "lanerunner"
    if (userType === "shipper") return "/shipper-dashboard-lanerunner";
    return "/broker-dashboard";
  };

  const handleClick = (next: ProductMode) => {
    setContext(next);
    navigate(destinationFor(next));
  };

  const h = size === "sm" ? "h-7" : "h-8";
  const segPad = size === "sm" ? "px-2.5 text-[10px]" : "px-3 text-[11px]";

  return (
    <div
      role="tablist"
      aria-label="Product context"
      className={cn(
        "inline-flex items-center rounded-full border border-border bg-background/70 p-0.5 flex-shrink-0",
        h,
        className,
      )}
    >
      <Segment
        active={context === "lr-direct"}
        label="LR DIRECT"
        activeClasses="bg-amber-400 text-amber-950 shadow-sm"
        onClick={() => handleClick("lr-direct")}
        padClass={segPad}
        showDot
      />
      <Segment
        active={context === "lanerunner"}
        label="LANERUNNER"
        activeClasses="bg-brand-teal-deep text-white shadow-sm"
        onClick={() => handleClick("lanerunner")}
        padClass={segPad}
      />
    </div>
  );
}

interface SegmentProps {
  active: boolean;
  label: string;
  activeClasses: string;
  padClass: string;
  onClick: () => void;
  showDot?: boolean;
}

function Segment({ active, label, activeClasses, padClass, onClick, showDot }: SegmentProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full font-bold uppercase tracking-wider transition-colors whitespace-nowrap h-full",
        padClass,
        active ? activeClasses : "text-muted-foreground hover:text-foreground",
      )}
    >
      {showDot && active && (
        <span className="h-1.5 w-1.5 rounded-full bg-amber-700" aria-hidden />
      )}
      {label}
    </button>
  );
}
